import moment from "moment/moment";

import { serverFormat } from "./dates";

// Matches Notification#category (`context.titleize`) for `context: :error`.
export const ERROR_MESSAGE_CATEGORY = "Error";

// Per banner type: the success toast, and (for types that produce a file) how
// to recognize the finished notification while polling. `scope` picks which
// banner fields the notification must share.
export const bannerTypes = {
  report: {
    hide: 10000,
    message: "Your report has been successfully generated!",
    scope: "owner",
    matchesTitle: () => true,
  },
  "export-excel": {
    hide: 5000,
    message: "Your Excel report has been successfully generated!",
    scope: "project",
    matchesTitle: title =>
      title.includes("Excel Report Ready for Download") ||
      title.includes("Delivery Addresses Excel Report Ready for Download"),
  },
  "export-inventory-report": {
    hide: 5000,
    message: "Your Inventory Report has been successfully generated!",
    scope: "project",
    matchesTitle: title => title.includes("Excel Report Ready for Download"),
  },
  "export-life-sciences-excel": {
    hide: 5000,
    message: "Your Life Sciences Excel report has been successfully generated!",
    scope: "project",
    matchesTitle: title => title.includes("Life Sciences Excel Report Ready for Download"),
  },
  download_images: {
    hide: 5000,
    message: "Your image file has been successfully generated!",
    scope: "project",
    matchesTitle: title => title.includes("Asset Images Ready for Download"),
  },
  "upload-template": {
    hide: 5000,
    message: "Your template has been successfully uploaded!",
    scope: "project",
    matchesTitle: title => title.includes("Labels Successfully Generated"),
  },
  "bulk-remove-assets": {
    hide: 5000,
    message: "Your assets have been successfully removed!",
  },
  "bulk-update-assets": {
    hide: 5000,
    message: "Your assets have been successfully updated!",
  },
  "find-and-replace-assets": {
    hide: 5000,
    message: "Your find and replace has been completed!",
  },
};

// Notifications carry `title`/`message`; newMessage payloads carry `title`/`text`.
export const notificationTitle = notification =>
  notification?.title || notification?.message || notification?.text || "";

const BILLING_REPORT_MARKERS = [
  "Billing Report (Prototype)",
  "Billing Report (Validation)",
  "Multi-project billing report",
];

// The backend has no report-kind field, so billing reports are recognized by
// the titles and messages billing_report_worker_helpers.rb writes.
export const isBillingReport = notification =>
  [notification?.title, notification?.message, notification?.text].some(text =>
    BILLING_REPORT_MARKERS.some(marker => text?.includes(marker))
  );

export const isPollableBannerType = type => Boolean(bannerTypes[type]?.matchesTitle);

const notificationTimestamp = notification => {
  const parsed = moment(notification.createdAt, [serverFormat, moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.valueOf();

  const fallback = moment(notification.createdAt).valueOf();
  return Number.isFinite(fallback) ? fallback : null;
};

const createdAfterBannerStart = (notification, banner) => {
  const createdAt = notificationTimestamp(notification);
  if (!createdAt || !banner.startedAt) return true;

  return createdAt >= moment(banner.startedAt).startOf("minute").valueOf() - 1000;
};

const scopeMatchers = {
  owner: (notification, banner) =>
    String(notification.ownerId) === String(banner.ownerId) &&
    (!notification.context || notification.context === "report"),
  project: (notification, banner) => {
    if (banner.ownerId && String(notification.ownerId) !== String(banner.ownerId)) return false;
    if (banner.projectId && String(notification.project?.id) !== String(banner.projectId)) return false;
    if (banner.clientId && String(notification.client?.id) !== String(banner.clientId)) return false;
    return true;
  },
};

export const canPollBanner = banner =>
  isPollableBannerType(banner.type) && (bannerTypes[banner.type].scope !== "owner" || Boolean(banner.ownerId));

export const matchesGeneratedFileBanner = (notification, banner) => {
  const config = bannerTypes[banner.type];
  if (!config?.matchesTitle || !notification?.link) return false;
  if (!config.matchesTitle(notificationTitle(notification))) return false;
  if (!scopeMatchers[config.scope](notification, banner)) return false;

  return createdAfterBannerStart(notification, banner);
};

export const notificationQueryVariablesForBanner = banner => {
  if (bannerTypes[banner.type]?.scope === "owner") {
    return { limit: 10, type: "report", ownerId: banner.ownerId };
  }

  return {
    limit: 10,
    projectId: banner.projectId,
    clientId: banner.projectId ? undefined : banner.clientId,
  };
};
